import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  scenariosAPI,
  metadataGroupsAPI,
  contentImproverAPI,
  aiTestingAgentAPI,
} from "../services/api";
import { useCommonData } from "../hooks/useCommonData";
import { useAgent } from "../contexts/AgentContext";
import { usePageTitle } from "../contexts/PageTitleContext";
import PageHeader from "../components/layout/PageHeader";
import usePlan from "../hooks/usePlan";
import AttributeGroupPicker from "../components/scenario/AttributeGroupPicker";
import ConditionalScenarioBuilder from "../components/scenario/ConditionalScenarioBuilder";
import AgentMetricsPicker from "../components/scenario/AgentMetricsPicker";
import { ChevronDown, ChevronUp, Sparkles, Eye, Users, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCan } from "../hooks/useCan";
import { usePostHog } from "@posthog/react";
import { findDefaultPersona } from "../constants/personas";

const CreateScenario = () => {
  const navigate = useWsNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const canWrite = useCan("scenario", "write");
  const { setPageTitle, setParentPage, setShowBackButton } = usePageTitle();
  useEffect(() => {
    setPageTitle(isEditMode ? "Edit Scenario" : "Create Scenario");
    setParentPage({ name: "Scenarios", href: "/live-simulations/scenario" });
    setShowBackButton(true);
    return () => {
      setPageTitle(null);
      setParentPage(null);
      setShowBackButton(false);
    };
  }, [isEditMode, setPageTitle, setParentPage, setShowBackButton]);
  const { isAuthenticated, user, currentWorkspace } = useAuth();
  const { showToast } = useToast();
  const posthog = usePostHog();
  const {
    agents,
    personas,
    loading: dataLoading,
    error: dataError,
  } = useCommonData();
  const { selectedAgent } = useAgent();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  // Per-agent metric selection: { "<agentId>": [metricIds] }
  const [agentMetrics, setAgentMetrics] = useState({});
  const [isAdditionalInfoOpen, setIsAdditionalInfoOpen] = useState(false);

  const [isImprovingInstructions, setIsImprovingInstructions] = useState(false);
  const [isImprovingOutcome, setIsImprovingOutcome] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    persona_id: "",
    voice_provider_id: "",
    testing_type: "voice",
    metadata: [],
    instructions: "",
    outcome: "",
    additional_info: {
      background_noise: "",
      talking_speed: 1,
      max_call_duration: 300,
      silence_timeout: 30,
    },
    tags: [],
    is_global: false,
    agent_ids: [],
    metadata_ids: [],
    metadata_group_ids: [],
    scenario_mode: "instruction",
    conditions: [],
    fallback_behavior: "role_fallback",
    source_call_observation_id: null,
  });
  // In create mode, default the persona to "Typical Male Caller" once personas
  // load. Edit mode keeps the scenario's saved persona.
  useEffect(() => {
    if (isEditMode || !personas || personas.length === 0) return;
    setFormData((prev) => {
      if (prev.persona_id) return prev;
      const persona = findDefaultPersona(personas);
      return persona ? { ...prev, persona_id: String(persona.id) } : prev;
    });
  }, [personas, isEditMode]);

  const backgroundNoises = [
    "None",
    "Office",
    "Cafe",
    "Street",
    "Home",
    "Public Transport",
    "Restaurant",
    "Park",
  ];

  const { planType, planBadge } = usePlan();

  const fetchGroups = () => {
    if (!currentWorkspace?.id) return;
    metadataGroupsAPI
      .getGroups(currentWorkspace.id)
      .then((data) => setGroups(Array.isArray(data) ? data : data?.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    fetchGroups();
  }, [currentWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const replayData = localStorage.getItem("replay_scenario_draft");
    if (replayData) {
      try {
        const draft = JSON.parse(replayData);
        setFormData((prev) => ({
          ...prev,
          name: draft.name || prev.name,
          instructions: draft.instructions || prev.instructions,
          outcome: draft.outcome || prev.outcome,
          scenario_mode: "conditional",
          conditions: draft.conditions || [],
          fallback_behavior: draft.fallback_behavior || "role_fallback",
          source_call_observation_id: draft.source_call_observation_id || null,
        }));
        localStorage.removeItem("replay_scenario_draft");
      } catch (e) {
        console.error("Failed to parse replay data:", e);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!currentWorkspace) {
      showToast("No workspace selected", "error");
      return;
    }

    let isMounted = true;
    const fetchData = async () => {
      // Only edit mode needs to load anything here; the per-agent metric picker
      // fetches each agent's metrics on demand.
      if (!isMounted || !isEditMode) return;

      try {
        const scenarioRes = await scenariosAPI.getScenario(
          id,
          currentWorkspace.id,
        );
        if (!isMounted) return;

        const scenario = scenarioRes.data.data;
        setFormData({
          name: scenario.name,
          persona_id: scenario.persona_id,
          voice_provider_id: scenario.voice_provider_id,
          testing_type: scenario.testing_type || "voice",
          metadata: scenario.metadata.map((meta) => ({
            id: meta.id,
            name: meta.name,
            value: meta.value,
            auto_generate: meta.auto_generate || false,
            generation_prompt: meta.generation_prompt || "",
          })),
          instructions: scenario.instructions,
          outcome: scenario.outcome,
          additional_info: scenario.additional_info || {
            background_noise: "",
            talking_speed: 1,
            max_call_duration: 300,
            opening_prompt: "",
          },
          tags: scenario.tags || [],
          is_global: scenario.is_global,
          agent_ids: scenario.agents.map((agent) => agent.id) || [],
          metadata_ids: scenario.metadata.map((meta) => meta.id) || [],
          metadata_group_ids: scenario.metadata_group_ids || [],
          scenario_mode: scenario.scenario_mode || "instruction",
          conditions: scenario.conditions || [],
          fallback_behavior: scenario.fallback_behavior || "role_fallback",
          source_call_observation_id:
            scenario.source_call_observation_id || null,
        });
        // Per-agent metric map (backend backfills legacy scenarios).
        setAgentMetrics(scenario.agent_metrics || {});
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching scenario:", error);
        showToast("Error fetching scenario", "error");
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, navigate, showToast, id, isEditMode, currentWorkspace]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdditionalInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      additional_info: {
        ...prev.additional_info,
        [name]: value,
      },
    }));
  };

  const handleTagsChange = (e) => {
    const input = e.target.value;
    // Split by both comma and space, then filter out empty strings
    const newTags = input.split(/[,\s]+/).filter((tag) => tag.trim());
    setFormData((prev) => ({
      ...prev,
      tags: [...new Set([...prev.tags, ...newTags])], // Remove duplicates
    }));
    e.target.value = ""; // Clear input after adding tags
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentWorkspace) {
      showToast("No workspace selected", "error");
      return;
    }

    // Required fields
    if (!formData.name.trim()) {
      showToast("Scenario name is required", "error");
      return;
    }
    if (!formData.persona_id) {
      showToast("Select a persona for this scenario", "error");
      return;
    }
    if (!formData.instructions.trim()) {
      showToast("Scenario instructions are required", "error");
      return;
    }

    // A non-global scenario must target at least one agent, and every selected
    // agent must have at least one metric to measure.
    if (!formData.is_global) {
      const agentIds = Object.keys(agentMetrics);
      if (agentIds.length === 0) {
        showToast("Select at least one agent for this scenario", "error");
        return;
      }
      const agentWithNoMetric = agentIds.find(
        (aid) => !(agentMetrics[aid] || []).length,
      );
      if (agentWithNoMetric) {
        showToast(
          "Each selected agent needs at least one metric to measure",
          "error",
        );
        return;
      }
    }

    setLoading(true);
    setError(null);
    const metadataIds = formData.metadata.map((meta) => meta.id);
    try {
      const payload = {
        scenario: {
          name: formData.name,
          persona_id: formData.persona_id,
          voice_provider_id: null,
          testing_type: formData.testing_type,
          metadata_ids: metadataIds,
          instructions: formData.instructions,
          outcome: formData.outcome,
          additional_info: {
            background_noise: formData.additional_info.background_noise,
            talking_speed: formData.additional_info.talking_speed,
            max_call_duration: formData.additional_info.max_call_duration,
          },
          tags: formData.tags,
          enabled_tools: [],
          // Per-agent metric selection drives agents + metrics on the backend.
          // Global scenarios target all agents → no per-agent map.
          ...(formData.is_global
            ? { agent_ids: [], metric_ids: [] }
            : { agent_metrics: agentMetrics }),
          is_global: formData.is_global,
          metadata_group_ids: formData.metadata_group_ids,
          scenario_mode: formData.scenario_mode,
          conditions:
            formData.scenario_mode === "conditional" ? formData.conditions : [],
          fallback_behavior: formData.fallback_behavior,
          source_call_observation_id: formData.source_call_observation_id,
        },
      };

      let response;
      if (isEditMode) {
        response = await scenariosAPI.updateScenario(
          id,
          payload,
          currentWorkspace.id,
        );
      } else {
        response = await scenariosAPI.createScenario(
          payload,
          currentWorkspace.id,
        );
      }

      if (!isEditMode) {
        posthog?.capture('scenario_created', {
          scenario_id: response?.data?.data?.id,
          agent_id: selectedAgent?.id,
          scenario_mode: formData.scenario_mode,
        });
      }
      showToast(
        `Scenario ${isEditMode ? "updated" : "created"} successfully`,
        "success",
      );
      navigate("/live-simulations/scenario");
    } catch (err) {
      console.error("Error saving scenario:", err);
      setError(err.response?.data?.error || "Failed to save scenario");
      showToast("Failed to save scenario", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleImproveInstructions = async () => {
    if (!formData.instructions) return;

    setIsImprovingInstructions(true);
    try {
      // The same field/button is "Role / Context" in conditional mode (a short
      // persona description) and "Scenario Instructions" otherwise (a numbered
      // step list) — send the matching improver so the format is preserved.
      const response = await contentImproverAPI.improveContent(
        formData.instructions,
        formData.scenario_mode === "conditional"
          ? "conditional_role"
          : "scenario_instructions",
      );
      setFormData((prev) => ({
        ...prev,
        instructions: response.improved_content,
      }));
    } catch (error) {
      console.error("Error improving content:", error);
    } finally {
      setIsImprovingInstructions(false);
    }
  };

  const handleImproveOutcome = async () => {
    if (!formData.outcome) return;

    setIsImprovingOutcome(true);
    try {
      const response = await contentImproverAPI.improveContent(
        formData.outcome,
        "scenario_outcome",
      );
      setFormData((prev) => ({
        ...prev,
        outcome: response.improved_content,
      }));
    } catch (error) {
      console.error("Error improving content:", error);
    } finally {
      setIsImprovingOutcome(false);
    }
  };

  return (
    <div className="px-4">
      <PageHeader
        title={isEditMode ? "Edit Scenario" : "Create Scenario"}
        description={
          isEditMode
            ? "Edit your test scenario details."
            : "Create a new test scenario for your agents."
        }
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Scenarios", path: "/scenarios" },
          {
            label: isEditMode ? "Edit Scenario" : "Create Scenario",
            path: isEditMode ? `/scenarios/${id}/edit` : "/scenarios/create",
          },
        ]}
        planBadge={planBadge}
      />

      {!canWrite && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-700 text-sm rounded-lg flex items-center gap-2">
          <Eye className="h-4 w-4 flex-shrink-0" /> View only — your role doesn't have write access
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8">
        {isEditMode && !formData.is_global && Object.keys(agentMetrics).length > 1 && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <Users className="mt-0.5 size-4 shrink-0 text-blue-600" />
            <p className="text-sm text-blue-800">
              <span className="font-semibold">
                This scenario is shared by {Object.keys(agentMetrics).length}{" "}
                assistants.
              </span>{" "}
              Editing it here updates the scenario for all of them. To change just
              one assistant, remove the others below or create a separate
              scenario.
            </p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-8">
          {/* First Column (1/3) */}
          <div className="space-y-6 border border-border rounded-lg p-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-foreground mb-2"
              >
                Scenario Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                disabled={!canWrite}
                className="w-full px-3 py-2 text-sm border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-muted disabled:text-muted-foreground"
                placeholder="e.g. Appointment Booking — Returning Patient"
                required
              />
            </div>

            <div className="mt-4">
              <label
                htmlFor="persona_id"
                className="block text-sm font-medium text-foreground mb-2"
              >
                Persona
              </label>
              <p className="text-xs text-muted-foreground mb-2">
                Select the character profile for this scenario
              </p>

              <select
                id="persona_id"
                value={formData.persona_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    persona_id: e.target.value,
                  }))
                }
                disabled={!canWrite}
                className="w-full px-3 py-2 text-sm border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-muted disabled:text-muted-foreground"
                required
              >
                <option value="">Select a persona</option>
                {personas.map((persona) => (
                  <option key={persona.id} value={persona.id}>
                    {persona.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label className="form-label text-sm">
                Conversation Attribute
              </label>
              <p className="form-hint mb-2 text-xs">
                Attach attribute groups whose values will be injected at runtime
              </p>
              <AttributeGroupPicker
                groups={groups}
                selectedIds={formData.metadata_group_ids}
                onChange={(ids) =>
                  setFormData((prev) => ({ ...prev, metadata_group_ids: ids }))
                }
                currentWorkspace={currentWorkspace}
                onGroupCreated={fetchGroups}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_global"
                checked={formData.is_global}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_global: e.target.checked,
                  }))
                }
                disabled={!canWrite}
                className="h-4 w-4 text-primary focus:ring-primary border-input rounded disabled:opacity-50"
              />
              <label
                htmlFor="is_global"
                className="ml-2 block text-sm text-foreground"
              >
                Mark as global
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground">
                Agents &amp; metrics
              </label>
              {!formData.is_global && (
                <p className="text-xs text-muted-foreground mb-2">
                  Add each agent this scenario runs against and choose its
                  metrics.
                </p>
              )}
              {formData.is_global ? (
                <div className="mt-2 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                  <Globe className="mt-0.5 size-4 shrink-0 text-blue-600" />
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">
                      This is a global scenario.
                    </span>{" "}
                    It applies across all your agents, so there's nothing to
                    select here. Each agent is measured by its own default
                    metrics when the scenario runs.
                  </p>
                </div>
              ) : (
                <AgentMetricsPicker
                  agents={agents}
                  value={agentMetrics}
                  onChange={setAgentMetrics}
                  clientId={currentWorkspace?.id}
                  disabled={!canWrite}
                  autoAddAgentId={!isEditMode ? selectedAgent?.id : undefined}
                />
              )}
            </div>
          </div>

          {/* Column 2 - 2/3 width */}
          <div className="col-span-2 border border-border rounded-lg p-4 space-y-6">
            {/* Scenario Mode — radio group */}
            <fieldset>
              <legend className="block text-sm font-medium text-foreground mb-2">
                Scenario Type
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  {
                    value: "instruction",
                    label: "Instruction",
                    description:
                      "Free-form instructions the tester will follow.",
                  },
                  {
                    value: "conditional",
                    label: "Conditional",
                    description: "Branching steps with IF / THEN conditions.",
                  },
                ].map((opt) => {
                  const selected = formData.scenario_mode === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                        selected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted"
                      }`}
                    >
                      <input
                        type="radio"
                        name="scenario_mode"
                        value={opt.value}
                        checked={selected}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            scenario_mode: opt.value,
                          }))
                        }
                        disabled={!canWrite}
                        className="mt-1 h-4 w-4 text-primary focus:ring-primary border-input disabled:opacity-50"
                      />
                      <span className="flex-1">
                        <span
                          className={`block text-sm font-medium ${selected ? "text-primary" : "text-foreground"}`}
                        >
                          {opt.label}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          {opt.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div>
              <div className="flex justify-between items-center mb-2">
                <div>
                  <label
                    htmlFor="instructions"
                    className="block text-sm font-medium text-foreground"
                  >
                    {formData.scenario_mode === "conditional"
                      ? "Role / Context"
                      : "Scenario Instructions"}
                  </label>
                  <p className="text-xs text-muted-foreground">
                    {formData.scenario_mode === "conditional"
                      ? "Describe who the test caller is pretending to be"
                      : "Detailed steps for the agent to follow"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleImproveInstructions}
                  disabled={!formData.instructions}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className="h-4 w-4" />
                  {isImprovingInstructions ? "Improving..." : "Improve with AI"}
                </button>
              </div>
              <div className="relative">
                <textarea
                  id="instructions"
                  value={formData.instructions}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      instructions: e.target.value,
                    }))
                  }
                  disabled={!canWrite}
                  className={`w-full h-32 px-3 text-sm py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-muted disabled:text-muted-foreground ${
                    isImprovingInstructions ? "animate-pulse bg-muted" : ""
                  }`}
                  placeholder={
                    formData.scenario_mode === "conditional"
                      ? "You are a patient calling to cancel an appointment"
                       : "You are calling to book a routine dental check-up and cleaning. You are a returning patient — your name is Sarah Miller. You expect the agent to verify your identity and find an available slot within the next two weeks."
                  }
                  required
                />
                {isImprovingInstructions && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/50 bg-opacity-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-2 h-2 bg-primary rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-primary rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-primary rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Outcome — show here for instruction mode */}
            {formData.scenario_mode !== "conditional" && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <label
                      htmlFor="outcome"
                      className="block text-sm font-medium text-foreground"
                    >
                      Scenario Outcome
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Expected results from the scenario
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleImproveOutcome}
                    disabled={!formData.outcome}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="h-4 w-4" />
                    {isImprovingOutcome ? "Improving..." : "Improve with AI"}
                  </button>
                </div>
                <div className="relative">
                  <textarea
                    id="outcome"
                    value={formData.outcome}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        outcome: e.target.value,
                      }))
                    }
                    disabled={!canWrite}
                    className={`w-full h-32 px-3 text-sm py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-muted disabled:text-muted-foreground ${
                      isImprovingOutcome ? "animate-pulse bg-muted" : ""
                    }`}
                    placeholder="The AI assistant successfully identifies and schedules a dental check-up and cleaning appointment for the user within the requested timeframe; The AI confirms the final appointment date, time, and service type ('check-up and cleaning' or similar) back to the user."
                    required
                  />
                  {isImprovingOutcome && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted/50 bg-opacity-50 rounded-md">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-2 h-2 bg-primary rounded-full animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-primary rounded-full animate-bounce"
                          style={{ animationDelay: "150ms" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-primary rounded-full animate-bounce"
                          style={{ animationDelay: "300ms" }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {formData.scenario_mode === "conditional" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Conversation Steps
                  </label>
                  <ConditionalScenarioBuilder
                    conditions={formData.conditions}
                    fallbackBehavior={formData.fallback_behavior}
                    onConditionsChange={(conditions) =>
                      setFormData((prev) => ({ ...prev, conditions }))
                    }
                    onFallbackChange={(fallback_behavior) =>
                      setFormData((prev) => ({ ...prev, fallback_behavior }))
                    }
                  />
                </div>

                {/* Outcome — after steps in conditional mode */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <label
                        htmlFor="outcome"
                        className="block text-sm font-medium text-foreground"
                      >
                        Scenario Outcome
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Expected results from the scenario
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleImproveOutcome}
                      disabled={!formData.outcome}
                      className="flex items-center gap-2 px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles className="h-4 w-4" />
                      {isImprovingOutcome ? "Improving..." : "Improve with AI"}
                    </button>
                  </div>
                  <textarea
                    id="outcome"
                    value={formData.outcome}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        outcome: e.target.value,
                      }))
                    }
                    disabled={!canWrite}
                    className="w-full h-32 px-3 text-sm py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-muted disabled:text-muted-foreground"
                    placeholder="The conversation completes all steps successfully."
                    required
                  />
                </div>
              </>
            )}

          </div>
        </div>

        {/* Additional Info Section */}
        <div className="mt-8">
          <button
            type="button"
            onClick={() => setIsAdditionalInfoOpen(!isAdditionalInfoOpen)}
            className="w-full flex justify-between items-center p-4 border border-border rounded-lg hover:bg-muted"
          >
            <h3 className="text-sm font-semibold text-foreground">Additional Information</h3>
            {isAdditionalInfoOpen ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>

          <AnimatePresence>
            {isAdditionalInfoOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="border border-border rounded-lg p-4 mt-4 overflow-hidden"
              >
                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Background Noise{" "}
                        <span className="text-xs text-muted-foreground">(voice only)</span>
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Simulated environment for the conversation
                      </p>
                      <select
                        name="background_noise"
                        value={formData.additional_info.background_noise}
                        onChange={handleAdditionalInfoChange}
                        className="mt-1 block w-full text-sm rounded-md border-input shadow-sm focus:border-primary focus:ring-primary"
                      >
                        {backgroundNoises.map((noise) => (
                          <option key={noise} value={noise}>
                            {noise}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Talking Speed{" "}
                        <span className="text-xs text-muted-foreground">(voice only)</span>
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Adjust the pace of the conversation
                      </p>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={formData.additional_info.talking_speed}
                        onChange={(e) =>
                          handleAdditionalInfoChange({
                            target: {
                              name: "talking_speed",
                              value: e.target.value,
                            },
                          })
                        }
                        className="w-full"
                      />
                      <span className="text-xs text-muted-foreground">
                        {formData.additional_info.talking_speed}x
                      </span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Max Call Duration (seconds)
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Maximum length of the conversation
                      </p>
                      <input
                        type="number"
                        name="max_call_duration"
                        value={formData.additional_info.max_call_duration}
                        onChange={handleAdditionalInfoChange}
                        className="mt-1 block w-full text-sm rounded-md border-input shadow-sm focus:border-primary focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Silence Timeout (seconds)
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Maximum time to wait for user response
                      </p>
                      <input
                        type="number"
                        name="silence_timeout"
                        value={formData.additional_info.silence_timeout}
                        onChange={handleAdditionalInfoChange}
                        min="1"
                        max="300"
                        className="mt-1 block w-full text-sm rounded-md border-input shadow-sm focus:border-primary focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Tags
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Keywords to categorize the scenario
                      </p>
                      <div className="mt-1">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary text-white"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-primary-dark"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder="Type tags and press space or comma"
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" ||
                              e.key === "," ||
                              e.key === " "
                            ) {
                              e.preventDefault();
                              handleTagsChange(e);
                            }
                          }}
                          className="mt-1 block w-full text-sm rounded-md border-input shadow-sm focus:border-primary focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {canWrite && (
          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update Scenario"
                  : "Create Scenario"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateScenario;
